import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import IconButton from "@material-ui/core/IconButton";
import MuiButton from "@material-ui/core/Button";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import React, { FC, useEffect, useState } from "react";
import { Checkbox, Tooltip } from "@material-ui/core";
import { GridColDef } from "@material-ui/data-grid";
import { Link } from "react-router-dom";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import CreateSegmentModalRedesign from "./CreateSegmentModalRedesign/CreateSegmentModalRedesign";
import DataGrid from "../../../components/redesign/modules/DataGrid";
import api from "../../../api";
import { ManageLink } from "../../../components/redesign/elements/ManageLink/ManageLink";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { errorToaster, infoToaster } from "../../../helpers/toasters";
import { setSelectedSegmentIds } from "../../../redux/reducers/campaignToCreate";

const PageRoot = styled("div")({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const Card = styled(Box)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
}));

const Toolbar = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: "16px 20px",
  flexShrink: 0,
});

const CreateButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  fontSize: 13,
  fontWeight: 500,
  padding: "6px 14px",
  borderRadius: 6,
  textTransform: "none",
  boxShadow: "none",
  minWidth: "unset",
  "&:hover": {
    background: theme.palette.brand.coralHover,
    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
  },
}));

const CoralCheckbox = styled(Checkbox)(({ theme }: { theme: Theme }) => ({
  "&.Mui-checked": {
    color: theme.palette.brand.coral,
  },
}));

const GridWrapper = styled("div")({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
});

const LoadingState = styled("div")({
  flex: 1,
  minHeight: 200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const EmptyState = styled("div")(({ theme }: { theme: Theme }) => ({
  flex: 1,
  minHeight: 200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "32px 20px",
  fontSize: 14,
  color: theme.palette.brand.gray500,
}));

const TagCell = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 4,
  width: "100%",
  overflow: "hidden",
});

const TagText = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  flex: 1,
  fontSize: 12,
  fontFamily: "monospace",
});

const NameCell = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 4,
  width: "100%",
  overflow: "hidden",
  "& > span": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

const WarnIcon = styled(ReportProblemOutlinedIcon)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.coral,
  fontSize: 18,
  flexShrink: 0,
  cursor: "help",
}));

const NO_LOADS_HINT = "This segment has no loads in the last 30 days. It may be misconfigured or was created recently.";

type Props = {
  isAttemptingToGoNext?: boolean;
  setIsAttemptingToGoNext?: (v: boolean) => void;
  handleNext?: () => void;
  panelMode?: boolean;
};

type SegmentRow = { id: string; name: string; tag: string; loads30Days: number; loadsYesterday: number };

const VisitorsRetargetingSegmentsView: FC<Props> = ({
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  handleNext,
  panelMode,
}) => {
  const isCampaignMode = !!setIsAttemptingToGoNext;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [segments, setSegments] = useState<SegmentRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const dispatch = useDispatch();
  const selectedSegmentIds = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedSegmentIds);
  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);
  const selectedAdLibAdvertiserId = selectedAdvertiser?.adLibAdvertiserId ?? null;

  const fetchSegments = async (advertiserId: number | null) => {
    if (!advertiserId) {
      setSegments([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const { payload } = await api.getSegments(advertiserId);
      const list = Array.isArray(payload?.segments) ? payload.segments : [];
      setSegments(
        list.map((s: any) => ({
          id: String(s.id),
          name: s.name ?? "",
          tag: s.pixelCode ?? "Processing...",
          loads30Days: s.loads30Days ?? 0,
          loadsYesterday: s.loadsYesterday ?? 0,
        })),
      );
    } catch {
      setSegments([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSegments(selectedAdLibAdvertiserId);
  }, [selectedAdLibAdvertiserId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAttemptingToGoNext || !setIsAttemptingToGoNext || !handleNext) return;
    if (selectedSegmentIds.length > 0) {
      handleNext();
    } else {
      errorToaster("Please, select at least one segment", 5000);
    }
    setIsAttemptingToGoNext(false);
  }, [isAttemptingToGoNext]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSegment = (id: string) => {
    if (selectedSegmentIds.includes(id)) {
      dispatch(setSelectedSegmentIds(selectedSegmentIds.filter((x) => x !== id)));
    } else {
      dispatch(setSelectedSegmentIds([...selectedSegmentIds, id]));
    }
  };

  const checkboxCol: GridColDef = {
    field: "checkbox",
    headerName: " ",
    disableColumnMenu: true,
    sortable: false,
    width: 60,
    // eslint-disable-next-line react/display-name
    renderCell: (params: any) => (
      <CoralCheckbox
        checked={selectedSegmentIds.includes(String(params.row.id))}
        onChange={() => toggleSegment(String(params.row.id))}
      />
    ),
  };

  const hasNoLoads = (row: SegmentRow) => row.loads30Days === 0 && row.loadsYesterday === 0;

  const baseColumns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      cellClassName: "primaryCell",
      // eslint-disable-next-line react/display-name
      renderCell: (params: any) => (
        <NameCell>
          {hasNoLoads(params.row) && (
            <Tooltip title={NO_LOADS_HINT}>
              <WarnIcon fontSize="small" />
            </Tooltip>
          )}
          <span title={String(params.value)}>{params.value}</span>
        </NameCell>
      ),
    },
    {
      field: "loads30Days",
      headerName: "Loads 30 Days",
      width: 160,
      minWidth: 160,
      renderCell: (params: any) => Number(params.value ?? 0).toLocaleString(),
    },
    {
      field: "loadsYesterday",
      headerName: "Loads Yesterday",
      width: 180,
      minWidth: 180,
      renderCell: (params: any) => Number(params.value ?? 0).toLocaleString(),
    },
    {
      field: "tag",
      headerName: "Tag",
      flex: 2,
      // eslint-disable-next-line react/display-name
      renderCell: (params: any) => {
        const isProcessing = params.value === "Processing...";
        return (
          <TagCell>
            <TagText title={String(params.value)}>{params.value}</TagText>
            <IconButton
              size="small"
              disabled={isProcessing}
              onClick={() => {
                navigator.clipboard.writeText(String(params.value));
                infoToaster("Tag copied to clipboard", 2000);
              }}
            >
              <FileCopyOutlinedIcon fontSize="small" />
            </IconButton>
          </TagCell>
        );
      },
    },
  ];

  const columns: GridColDef[] = isCampaignMode || panelMode ? [checkboxCol, ...baseColumns] : baseColumns;

  return (
    <PageRoot>
      {panelMode && (
        <ManageLink>
          To create a new segment, go to <Link to="/visitors-retargeting">Visitor Retargeting</Link>.
        </ManageLink>
      )}
      <Card>
        {!panelMode && (
          <Toolbar>
            <CreateButton onClick={() => setIsModalOpen(true)}>Create New Segment</CreateButton>
          </Toolbar>
        )}

        <GridWrapper>
          {isLoading ? (
            <LoadingState>
              <CircularProgress size={32} />
            </LoadingState>
          ) : segments.length === 0 ? (
            <EmptyState>
              {panelMode ? (
                <>
                  No segments found. <Link to="/visitors-retargeting">Go to Visitor Retargeting →</Link>
                </>
              ) : (
                "No segments available."
              )}
            </EmptyState>
          ) : (
            <DataGrid columns={columns} rows={segments} autoHeight={panelMode} />
          )}
        </GridWrapper>
      </Card>

      <CreateSegmentModalRedesign
        isOpen={isModalOpen}
        shouldSetDefaultAdvertiser={isCampaignMode}
        handleClose={() => {
          setIsModalOpen(false);
          setTimeout(() => fetchSegments(selectedAdLibAdvertiserId), 1000);
        }}
      />
    </PageRoot>
  );
};

export default VisitorsRetargetingSegmentsView;
